<script lang="ts">
	import { combineLatest, map, Observable, of, tap } from "rxjs";

	import type { LandingItemContext } from "$lib/def/landing";
	import { BannerType, deluxe$, setDeluxeBanner, type BannerArrays, type BannerTypeOption, type DisplayBanner } from "$lib/serv/banners";
	import type { Cache } from "$lib/serv/core/cache";
	import { fetch$ } from "$lib/serv/core/cache";
	import { getDB, KEY_ITEM_MERCH, SPKEYS } from "$lib/serv/food-landing";
	import { queryFrom } from "$lib/util/url";
	import { getContext, onDestroy } from "svelte";
  //import Carousel, { type CarouselType } from "$lib/ui/layout/Carousel.svelte";
	import CarouselV3 from "$lib/ui/layout/CarouselV3.svelte";
	import LazyImage from "$lib/ui/shared/LazyImage.svelte";
  import ItemMerchBanner from "$lib/ui/home/food/ItemMerchBanner.svelte";
	
	type Props = {
		endpoint: string;
		searchParams: Record<string, string>;
		id: string;
	};

	let { endpoint, searchParams, id = '' }: Props = $props();

	const { base, cacheAge } = getContext('endpoints') as LandingItemContext;

	// let cache = setupBanners();
	let cache: Observable<Cache<DisplayBanner[]> & { bannerType: BannerTypeOption }> = endpoint ? combineLatest([fetch$<BannerArrays>(new URL(base + endpoint), SPKEYS, { db: getDB(), msMaxCacheAge: cacheAge.medium }).pipe(
			map((cache) => ({
				...cache,
				payload: (cache.payload?.small ?? cache.payload?.large ?? cache.payload?.deluxe) as DisplayBanner[],
				bannerType: cache.payload ? Object.keys(cache.payload)[0] as BannerTypeOption : '' as BannerTypeOption,
			})),
			tap((cache) => {
				if (cache.bannerType === BannerType.Deluxe) setDeluxeBanner(cache.payload);
			}),
		),
		deluxe$
	]).pipe(
		map(([c, deluxe]) => {
			// TODO: @Dan Fix deluxe banners being added late
			if (deluxe && c.bannerType === BannerType.Large) c.payload.unshift(...deluxe);
			return c;
		}),
	) : of({payload:[], bannerType:''});

	// let resize: () => void = $state(() => {});
	// const cacheSub = cache?.subscribe(() => resize());

	const link = (banner: DisplayBanner, type: string, params?: typeof searchParams) => {
    if (banner.admin_campaign_type === KEY_ITEM_MERCH && (banner.sub_type === "group" || banner.sub_type === "smart")) {
		  return `?${queryFrom(params).set(KEY_ITEM_MERCH, banner.admin_campaign_uuid).toString()}`;
    }

		if (!banner.display?.title) return banner.display?.url;
		return `?${queryFrom(params).set('lp', `${banner._uuid}_${type}`).toString()}`;
	};

	const newTab = (display: any) => display && !display.title && display.url?.startsWith('http') ? '_blank' : null;

	// onDestroy(() => cacheSub?.unsubscribe());

  const merchBannerSubtypes = ["restaurant", "single"];
</script>

{#if $cache?.bannerType !== BannerType.Deluxe}
	<CarouselV3 cache={$cache as Cache<DisplayBanner[]>} type={`banners ${$cache?.bannerType}`} {id} key={(p: DisplayBanner[]) => p as DisplayBanner[]}>
		{#snippet children({ item: banner, index })}
			{#if banner.admin_campaign_type === KEY_ITEM_MERCH && merchBannerSubtypes.includes(banner.sub_type)}
        <ItemMerchBanner {banner} {endpoint} {searchParams} {id} {index}/>
			{:else}
				<a href={link(banner, banner.type.replace('_banner', '') || $cache?.bannerType, searchParams)}
					target={newTab(banner.display)}
					class="item"
					class:cropped={!banner.image_only && banner.cropped}
					class:portrait={!banner.image_only && banner.portrait}
					class:portraitAndCropped={!banner.image_only && banner.portrait && banner.cropped}
					style:background-color={banner.colours?.background}
					style:color={banner.colours?.title}
					data-testid={`banner_${id}_item_${index}`}
					>
					<div class="relative">
						<div class="image">
							<LazyImage image={[banner.image, 500]} alt={banner.title}></LazyImage>
						</div>
						{#if !banner.image_only}
							<div class="text">
								<h4 class="title body-l bold">{banner.title}</h4>
								{#if banner.sub_title}
									<p class="subtitle body-l book" style:color={banner.colours?.subtitle}>{banner.sub_title}</p>
								{/if}
								{#if banner.call_to_action}
									<button style:background-color={banner.colours?.button} style:color={banner.colours?.button_text}>{banner.call_to_action}</button>
								{/if}
							</div>
						{/if}
					</div>
				</a>
			{/if}
		{/snippet}
	</CarouselV3>
{/if}
